import React from 'react';
import { TokenData } from '../hooks/useBondingCurve';
import { MIGRATION_THRESHOLD } from '../config/contracts';

interface TokenCardProps {
  token: TokenData;
  onClick: (token: TokenData) => void;
  price24hAgo?: string; // 24h 前价格，用于计算涨跌幅
}

function generateColor(str: string): string {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  const colors = [
    ['#00d4ff', '#7c4dff'],
    ['#00e676', '#00bcd4'],
    ['#ff6d00', '#ff4081'],
    ['#ffd600', '#ff6d00'],
    ['#7c4dff', '#e040fb'],
    ['#00bcd4', '#00e676'],
    ['#ff4081', '#7c4dff'],
  ];
  return `linear-gradient(135deg, ${colors[Math.abs(hash) % colors.length].join(', ')})`;
}

function formatNumber(num: string): string {
  const n = parseFloat(num);
  if (n >= 1e9) return (n / 1e9).toFixed(2) + 'B';
  if (n >= 1e6) return (n / 1e6).toFixed(2) + 'M';
  if (n >= 1e3) return (n / 1e3).toFixed(2) + 'K';
  return n.toFixed(4);
}

function timeAgo(timestamp: number): string {
  const diff = Date.now() / 1000 - timestamp;
  if (diff < 60) return '刚刚';
  if (diff < 3600) return Math.floor(diff / 60) + ' 分钟前';
  if (diff < 86400) return Math.floor(diff / 3600) + ' 小时前';
  return Math.floor(diff / 86400) + ' 天前';
}

const TokenCard: React.FC<TokenCardProps> = ({ token, onClick, price24hAgo }) => {
  const gradient = generateColor(token.symbol);
  const progress = token.progress;
  const isMigrated = token.migrated;

  // 计算24h涨跌幅
  let change24h: number | null = null;
  if (price24hAgo && parseFloat(price24hAgo) > 0) {
    const cur = parseFloat(token.currentPrice);
    const prev = parseFloat(price24hAgo);
    change24h = ((cur - prev) / prev) * 100;
  }

  return (
    <div
      style={{
        ...styles.card,
        ...(isMigrated ? styles.cardMigrated : {}),
      }}
      onClick={() => onClick(token)}
      onMouseEnter={e => {
        (e.currentTarget as HTMLDivElement).style.transform = 'translateY(-4px)';
        (e.currentTarget as HTMLDivElement).style.borderColor = '#243352';
        (e.currentTarget as HTMLDivElement).style.boxShadow = '0 12px 40px rgba(0,0,0,0.5)';
      }}
      onMouseLeave={e => {
        (e.currentTarget as HTMLDivElement).style.transform = 'translateY(0)';
        (e.currentTarget as HTMLDivElement).style.borderColor = '#1e2d4a';
        (e.currentTarget as HTMLDivElement).style.boxShadow = '0 4px 24px rgba(0,0,0,0.4)';
      }}
    >
      {/* Header */}
      <div style={styles.cardHeader}>
        <div style={{ ...styles.tokenIcon, background: gradient }}>
          {token.symbol.slice(0, 2).toUpperCase()}
        </div>
        <div style={styles.tokenMeta}>
          <div style={styles.tokenName}>{token.name}</div>
          <div style={styles.tokenSymbol}>${token.symbol}</div>
        </div>
        <div style={styles.statusBadge}>
          {isMigrated ? (
            <span style={styles.badgeMigrated}>🥞 PancakeSwap</span>
          ) : (
            <span style={styles.badgeActive}>● 曲线交易中</span>
          )}
        </div>
      </div>

      {/* Price */}
      <div style={styles.priceRow}>
        <div>
          <div style={styles.priceLabel}>当前价格</div>
          <div style={styles.priceValue}>
            ${formatNumber(token.currentPrice)}
            <span style={styles.priceUnit}> USDT</span>
          </div>
          {change24h !== null && (
            <div style={{
              ...styles.change24h,
              color: change24h >= 0 ? '#00e676' : '#ff4d6d',
            }}>
              {change24h >= 0 ? '▲' : '▼'} {Math.abs(change24h).toFixed(2)}% <span style={styles.change24hLabel}>24h</span>
            </div>
          )}
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={styles.priceLabel}>流动性储备</div>
          <div style={styles.reserveValue}>
            ${parseFloat(token.reserveUSDT).toLocaleString()}
          </div>
        </div>
      </div>

      {/* Progress Bar */}
      {!isMigrated && (
        <div style={styles.progressSection}>
          <div style={styles.progressHeader}>
            <span style={styles.progressLabel}>迁移进度</span>
            <span style={styles.progressValue}>
              ${parseFloat(token.reserveUSDT).toLocaleString()} / ${parseInt(MIGRATION_THRESHOLD).toLocaleString()} USDT
            </span>
          </div>
          <div style={styles.progressBar}>
            <div
              style={{
                ...styles.progressFill,
                width: `${progress}%`,
                background: progress >= 80
                  ? 'linear-gradient(90deg, #ffd600, #ff6d00)'
                  : progress >= 50
                  ? 'linear-gradient(90deg, #00d4ff, #7c4dff)'
                  : 'linear-gradient(90deg, #00e676, #00d4ff)',
              }}
            />
          </div>
          <div style={styles.progressPct}>{progress.toFixed(1)}%</div>
        </div>
      )}

      {/* Description */}
      {token.description && (
        <div style={styles.description}>
          {token.description.length > 80 ? token.description.slice(0, 80) + '…' : token.description}
        </div>
      )}

      {/* Footer */}
      <div style={styles.cardFooter}>
        <div style={styles.footerItem}>
          <span style={styles.footerLabel}>总供应</span>
          <span style={styles.footerValue}>{formatNumber(token.totalSupply)}</span>
        </div>
        <div style={styles.footerItem}>
          <span style={styles.footerLabel}>创建</span>
          <span style={styles.footerValue}>{timeAgo(token.createdAt)}</span>
        </div>
        <div style={styles.footerItem}>
          <span style={styles.footerLabel}>创建者</span>
          <span style={styles.footerValue}>
            {token.creator.slice(0, 6)}...{token.creator.slice(-4)}
          </span>
        </div>
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  card: {
    background: '#131929',
    border: '1px solid #1e2d4a',
    borderRadius: 16,
    padding: '20px',
    cursor: 'pointer',
    transition: 'all 0.25s ease',
    position: 'relative',
    overflow: 'hidden',
    boxShadow: '0 4px 24px rgba(0,0,0,0.4)',
  },
  cardMigrated: {
    opacity: 0.75,
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    marginBottom: 16,
  },
  tokenIcon: {
    width: 46,
    height: 46,
    borderRadius: 12,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 16,
    fontWeight: 800,
    color: '#fff',
    flexShrink: 0,
  },
  tokenMeta: {
    flex: 1,
  },
  tokenName: {
    fontSize: 15,
    fontWeight: 700,
    color: '#e8eef7',
    marginBottom: 2,
  },
  tokenSymbol: {
    fontSize: 12,
    color: '#6b7d99',
    fontWeight: 500,
  },
  statusBadge: {},
  badgeActive: {
    fontSize: 11,
    color: '#00e676',
    background: 'rgba(0,230,118,0.1)',
    border: '1px solid rgba(0,230,118,0.2)',
    borderRadius: 6,
    padding: '3px 8px',
    fontWeight: 600,
    whiteSpace: 'nowrap' as const,
  },
  badgeMigrated: {
    fontSize: 11,
    color: '#ffd600',
    background: 'rgba(255,214,0,0.1)',
    border: '1px solid rgba(255,214,0,0.2)',
    borderRadius: 6,
    padding: '3px 8px',
    fontWeight: 600,
    whiteSpace: 'nowrap' as const,
  },
  priceRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    marginBottom: 16,
    padding: '12px 14px',
    background: 'rgba(255,255,255,0.03)',
    borderRadius: 10,
    border: '1px solid rgba(255,255,255,0.05)',
  },
  priceLabel: {
    fontSize: 11,
    color: '#6b7d99',
    marginBottom: 4,
    fontWeight: 500,
  },
  priceValue: {
    fontSize: 18,
    fontWeight: 700,
    color: '#00d4ff',
  },
  priceUnit: {
    fontSize: 11,
    color: '#6b7d99',
    fontWeight: 400,
  },
  change24h: {
    fontSize: 12,
    fontWeight: 700,
    marginTop: 4,
  },
  change24hLabel: {
    fontSize: 10,
    fontWeight: 400,
    color: '#6b7d99',
  },
  reserveValue: {
    fontSize: 16,
    fontWeight: 700,
    color: '#e8eef7',
  },
  progressSection: {
    marginBottom: 16,
  },
  progressHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  progressLabel: {
    fontSize: 12,
    color: '#6b7d99',
    fontWeight: 500,
  },
  progressValue: {
    fontSize: 11,
    color: '#6b7d99',
  },
  progressBar: {
    height: 6,
    background: 'rgba(255,255,255,0.06)',
    borderRadius: 3,
    overflow: 'hidden',
    marginBottom: 4,
  },
  progressFill: {
    height: '100%',
    borderRadius: 3,
    transition: 'width 0.5s ease',
  },
  progressPct: {
    fontSize: 11,
    color: '#6b7d99',
    textAlign: 'right' as const,
  },
  description: {
    fontSize: 12,
    color: '#6b7d99',
    lineHeight: 1.5,
    marginBottom: 12,
    padding: '8px 10px',
    background: 'rgba(255,255,255,0.02)',
    borderRadius: 8,
    borderLeft: '2px solid #1e2d4a',
  },
  cardFooter: {
    display: 'flex',
    justifyContent: 'space-between',
    borderTop: '1px solid rgba(255,255,255,0.05)',
    paddingTop: 12,
  },
  footerItem: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 2,
  },
  footerLabel: {
    fontSize: 10,
    color: '#3d5070',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
  },
  footerValue: {
    fontSize: 12,
    color: '#6b7d99',
    fontWeight: 500,
  },
};


export default TokenCard;
